import {GENDER_KTS, Page} from "@/libwyze-shared/types";
import React, {ReactElement, useEffect, useRef, useState} from "react";
import {Box, Pagination, Typography} from "@mui/material";
import {StyledBoxColumn, StyledBoxRow} from "@/shared/StyledComponents";
import {CheckinScreenType, CheckinState} from "./CheckinPlayerPreviewFragment";
import {
  CheckinButton,
  CheckinLargeButton,
  CheckinLargeDatePicker,
  CheckinLargeTextField
} from "@/checkin-shared/Components";
import {ArrowForwardOutlined} from "@mui/icons-material";
import {PAGE_FRAGMENT_XSM_WIDTH, PD_MD, PD_XLG, PD_XSM} from "@/shared/dimens";
import {Keyboard} from "./components/Keyboard";
import {Numpad} from "./components/Numpad";
import {CheckinRegister} from "../../checkin-common/types";

const TOTAL_STEPS = 3;

function FormField(props: { label: string, children: ReactElement }): ReactElement {
  return <StyledBoxColumn style={{gap: PD_XSM, width: "100%"}}>
    <Typography variant="caption" style={{fontWeight: 600, textTransform: "uppercase", opacity: 0.5}}>
      {props.label}
    </Typography>
    {props.children}
  </StyledBoxColumn>;
}

type StepConfig = {
  title: string,
  subtitle: string,
}

const STEPS: StepConfig[] = [
  {title: "What's your name?", subtitle: "The name on your ID, please."},
  {title: "Let us know more", subtitle: "We use this to find or create your patient record."},
  {title: "How can we reach you?", subtitle: "Enter your phone number and email address."},
];

export function NewPatientFormScreen(props: {
  practicePage: Page,
  pushScreenType: (st: CheckinScreenType) => void,
  checkinState: CheckinState,
  setCheckinState: (checkin: CheckinState) => void,
  setOnBackOverride: (handler: (() => boolean) | null) => void,
}): ReactElement {
  const [step, setStep] = useState(0);
  const [activeField, setActiveField] = useState<string>("firstname");

  useEffect(() => {
    props.setOnBackOverride(() => {
      if (step > 0) {
        setStep(step - 1);
        if (step - 1 === 0) setActiveField("firstname");
        if (step - 1 === 1) setActiveField("firstname");
        return true;
      }
      return false;
    });
    return () => props.setOnBackOverride(null);
  }, [step]);
  const register = props.checkinState.register;

  const firstnameRef = useRef<HTMLInputElement>(null);
  const lastnameRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  const updateRegister = (field: Partial<typeof register>) => {
    props.setCheckinState({
      ...props.checkinState,
      register: Object.assign(new CheckinRegister(), {...register, ...field}),
    });
  };

  const handleKeyboardKey = (key: string) => {
    const fieldMap: Record<string, { value: string | number, setter: (v: any) => void }> = {
      firstname: {value: register.firstname || "", setter: v => updateRegister({firstname: v as string})},
      lastname: {value: register.lastname || "", setter: v => updateRegister({lastname: v as string})},
      gender: {value: register.gender || "", setter: v => updateRegister({gender: v as string})},
      dateOfBirth: {value: register.dateOfBirth || "", setter: v => updateRegister({dateOfBirth: v as null})},
      email: {value: register.email || "", setter: v => updateRegister({email: v as string})},
      phone: {value: register.phoneNumber || "", setter: v => updateRegister({phoneNumber: v as string})},
    };
    const field = fieldMap[activeField];
    if (!field) return;
    if (key === "BACKSPACE" && (typeof field.value === "string")) {
      field.setter(field.value.slice(0, -1));
    } else {
      field.setter(field.value + key);
    }
  };

  const canAdvance = () => {
    switch (step) {
      case 0:
        return Boolean(register.firstname) && Boolean(register.lastname);
      case 1:
        return Boolean(register.dateOfBirth) && Boolean(register.gender);
      case 2:
        return Boolean(register.phoneNumber) && Boolean(register.email);
      default:
        return false;
    }
  };

  const handleContinue = () => {
    if (step < TOTAL_STEPS - 1) {
      const nextStep = step + 1;
      setStep(nextStep);
      if (nextStep === 2) setActiveField("phone");
    } else {
      props.pushScreenType(CheckinScreenType.VISIT_REASON);
    }
  };

  const stepConfig = STEPS[step];

  const renderStepFields = (): ReactElement => {
    switch (step) {
      case 0:
        return <StyledBoxRow style={{width: "100%", gap: PD_MD}}>
          <FormField label="First name">
            <CheckinLargeTextField
              autoFocus={true}
              inputRef={firstnameRef}
              style={{flexGrow: 1}}
              placeholder="e.g. John"
              value={register.firstname}
              onFocus={() => setActiveField("firstname")}
              onChange={e => updateRegister({firstname: e.target.value || ""})}
            />
          </FormField>
          <FormField label="Last name">
            <CheckinLargeTextField
              inputRef={lastnameRef}
              style={{flexGrow: 1}}
              placeholder="e.g. Doe"
              value={register.lastname}
              onFocus={() => setActiveField("lastname")}
              onChange={e => updateRegister({lastname: e.target.value || ""})}
            />
          </FormField>
        </StyledBoxRow>;
      case 1:
        return <StyledBoxColumn style={{gap: PD_XLG}}>
          <FormField label="Date of birth">
            <CheckinLargeDatePicker
              onChange={date => updateRegister({dateOfBirth: date.toDate().getTime()})}
              autoFocus={true}/>
          </FormField>
          <FormField label="Gender">
            <StyledBoxRow style={{gap: PD_XLG}}>
              {GENDER_KTS.values.map(value => <CheckinButton
                variant={register.gender === value.key ? "contained" : "outlined"}
                onClick={() => updateRegister({gender: value.key})}
                key={value.key}>
                {value.text}
              </CheckinButton>)}
            </StyledBoxRow>
          </FormField>
        </StyledBoxColumn>;
      case 2:
        return <StyledBoxRow style={{width: "100%", gap: PD_MD}}>
          <FormField label="Phone number">
            <CheckinLargeTextField
              autoFocus={true}
              inputRef={phoneRef}
              style={{flexGrow: 1}}
              placeholder="e.g. +1 555 123 4567"
              value={register.phoneNumber || ""}
              onFocus={() => setActiveField("phone")}
              onChange={e => updateRegister({phoneNumber: e.target.value || ""})}
            />
          </FormField>
          <FormField label="Email">
            <CheckinLargeTextField
              inputRef={emailRef}
              style={{flexGrow: 1}}
              placeholder="e.g. alex@email.com"
              value={register.email || ""}
              onFocus={() => setActiveField("email")}
              onChange={e => updateRegister({email: e.target.value || ""})}
            />
          </FormField>
        </StyledBoxRow>;
      default:
        return null;
    }
  };

  const renderInputPanel = (): ReactElement => {
    switch (step) {
      case 0:
        return <Keyboard onKey={handleKeyboardKey}/>;
      case 1:
        return null;
      case 2:
        return activeField === "phone"
          ? <Numpad onKey={handleKeyboardKey}/>
          : <Keyboard onKey={handleKeyboardKey}/>;
      default:
        return null;
    }
  };

  return <StyledBoxColumn style={{width: "100%", height: "100%"}}>
    <StyledBoxRow style={{width: "100%", flex: 1, alignItems: "flex-start", gap: 48}}>
      <StyledBoxColumn style={{flex: 1, alignItems: "flex-start"}}>
        <Pagination
          count={TOTAL_STEPS}
          page={step + 1}
          hidePrevButton
          hideNextButton
          size="small"
          sx={{pointerEvents: "none"}}
        />
        <Typography variant={"h2"} style={{fontWeight: 900}}>
          {stepConfig.title}
        </Typography>
        <Typography style={{fontSize: "100%", opacity: 0.5}}>
          {stepConfig.subtitle}
        </Typography>
        <Box style={{marginTop: PD_MD}}/>
        {renderStepFields()}
      </StyledBoxColumn>
      <StyledBoxColumn style={{flexShrink: 0, width: PAGE_FRAGMENT_XSM_WIDTH, paddingTop: 48}}>
        <CheckinLargeButton
          endIcon={<ArrowForwardOutlined style={{fontSize: "125%"}}/>}
          disabled={!canAdvance()}
          onClick={handleContinue}>
          {step < TOTAL_STEPS - 1 ? "Continue" : "Finish"}
        </CheckinLargeButton>
      </StyledBoxColumn>
    </StyledBoxRow>
    {renderInputPanel()}
  </StyledBoxColumn>;
}
